// Main navigation (interface refresh, screen 2): a collapsible icon rail on the left (2A) that
// slides out over the page on hover (user request) and can be pinned open (remembered per browser); on phones a bottom tab bar with the main sections and "More" for the
// rest. Copied in web/ and crm/ with each app's own sections.
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BRAND } from '../../../ui/brand';
import { icons } from './Icons';
import { Link, NavLink } from './Links';

// A section link marked active by its own rule (e.g. a vehicle's Journeys tab belongs to the map).
function ItemLink({ item, className, children, ...rest }: { item: RailItem; className: string; children: ReactNode; title?: string; 'aria-label'?: string }) {
  const { pathname } = useLocation();
  if (!item.match) return <NavLink to={item.to} end={item.end} className={className} {...rest}>{children}</NavLink>;
  const active = item.match(pathname);
  return <Link to={item.to} className={`${className}${active ? ' active' : ''}`} aria-current={active ? 'page' : undefined} {...rest}>{children}</Link>;
}

export interface RailItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
  badge?: ReactNode;       // e.g. the gateway warning dot
  phone?: boolean;         // on the phone's bottom bar (else under More)
  match?: (path: string) => boolean;  // when it counts as the open section (default: by its address)
}

const KEY = 'ff.rail.open';
const readOpen = () => { try { return localStorage.getItem(KEY) === '1'; } catch { return false; } };

export function Rail({ product, items, user, onLogout }: {
  product: string;
  items: RailItem[];
  user: { name: string; email: string };
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(readOpen);   // pinned open with the arrow
  const [peek, setPeek] = useState(false);       // slid out while the mouse is over it
  const peekTimer = useRef<number>();
  const [more, setMore] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { try { localStorage.setItem(KEY, open ? '1' : '0'); } catch { /* private mode */ } }, [open]);
  useEffect(() => { setMore(false); setPeek(false); }, [pathname]);
  // Hover: slide out after a moment (so passing over it doesn't), back in when the mouse leaves.
  const enter = () => { window.clearTimeout(peekTimer.current); peekTimer.current = window.setTimeout(() => setPeek(true), 150); };
  const leave = () => { window.clearTimeout(peekTimer.current); setPeek(false); };
  const wide = open || peek;
  const initials = user.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const phoneItems = items.filter((i) => i.phone);
  const moreItems = items.filter((i) => !i.phone);

  return (
    <>
      <div className={`rail-slot${open ? ' pinned' : ''}`}>
      <nav className={`rail${wide ? ' open' : ''}${peek && !open ? ' peek' : ''}`} aria-label="Main"
        onMouseEnter={enter} onMouseLeave={leave}
        onFocus={() => setPeek(true)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setPeek(false); }}>
        <NavLink to="/" end className="rail-brand" aria-label={`${BRAND.name} ${product}`}>
          <img src={BRAND.emblem} alt="" width={32} height={32} />
          <span className="rail-label"><b>{BRAND.name}</b><small>{product}</small></span>
        </NavLink>
        <ul className="rail-items">
          {items.map((i) => (
            <li key={i.to}>
              <ItemLink item={i} className="rail-item" title={wide ? undefined : i.label} aria-label={i.label}>
                <span className="rail-icon">{i.icon}{i.badge}</span>
                <span className="rail-label">{i.label}</span>
              </ItemLink>
            </li>
          ))}
        </ul>
        <div className="rail-foot">
          <NavLink to="/account" className="rail-item" title={wide ? undefined : `${user.name} · ${user.email}`} aria-label={`My account (${user.name})`}>
            <span className="rail-icon"><span className="rail-avatar">{initials}</span></span>
            <span className="rail-label">{user.name}</span>
          </NavLink>
          <button type="button" className="rail-item" onClick={onLogout} title={wide ? undefined : 'Log out'} aria-label="Log out">
            <span className="rail-icon">{icons.logout}</span>
            <span className="rail-label">Log out</span>
          </button>
          <button type="button" className="rail-item rail-toggle" onClick={() => { setOpen(!open); setPeek(false); }} aria-pressed={open}
            title={open ? 'Let the menu slide in again' : 'Keep the menu open'} aria-label={open ? 'Unpin menu' : 'Keep menu open'}>
            <span className="rail-icon">{open ? icons.collapse : icons.expand}</span>
            <span className="rail-label">{open ? 'Unpin menu' : 'Keep open'}</span>
          </button>
        </div>
      </nav>
      </div>

      {/* Phones: bottom tab bar */}
      <nav className="tabbar" aria-label="Main">
        {phoneItems.map((i) => (
          <ItemLink key={i.to} item={i} className="tab">
            <span className="rail-icon">{i.icon}{i.badge}</span>
            <span>{i.label}</span>
          </ItemLink>
        ))}
        <button type="button" className={`tab${more ? ' active' : ''}`} onClick={() => setMore(!more)} aria-expanded={more}>
          <span className="rail-icon">{icons.more}{moreItems.some((i) => i.badge) && moreItems.find((i) => i.badge)!.badge}</span>
          <span>More</span>
        </button>
      </nav>
      {more && (
        <div className="sheet-backdrop" onClick={() => setMore(false)}>
          <div className="sheet" role="dialog" aria-label="More" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <span className="rail-avatar">{initials}</span>
              <span><b>{user.name}</b><small>{user.email}</small></span>
              <button type="button" className="sheet-close" onClick={() => setMore(false)} aria-label="Close">{icons.close}</button>
            </div>
            {moreItems.map((i) => (
              <ItemLink key={i.to} item={i} className="sheet-item">
                <span className="rail-icon">{i.icon}{i.badge}</span>{i.label}
              </ItemLink>
            ))}
            <NavLink to="/account" className="sheet-item"><span className="rail-icon">{icons.user}</span>My account</NavLink>
            <button type="button" className="sheet-item" onClick={onLogout}><span className="rail-icon">{icons.logout}</span>Log out</button>
          </div>
        </div>
      )}
    </>
  );
}
